import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import { CurrencyInr, Lightning, Headphones, PlayCircle, Rocket, ArrowRight, Sparkle, Info, Star, Moon, YoutubeLogo, ArrowSquareOut } from "@phosphor-icons/react";

// --- Curated wealth subliminal playlist ----------------------------------
// Each entry is a hand-picked YouTube video that is:
//   - in the wealth / abundance / money-magnet subliminal niche
//   - has accumulated millions of views (social-proofed)
//   - is a stable channel that won't suddenly delete the video
// `note` captures WHY this one is worth a listen — gives users a reason to
// pick a specific track instead of just scrolling past.
const TRACKS = [
  {
    id: "4tk_qvauc9Q",
    title: "888 Hz · 88 Hz · 8 Hz · Abundance Pyramid — Gate to Wealth",
    channel: "Soothing Relaxation",
    duration: "1 hr",
    views_label: "Millions of views",
    why: "The 8-Hz angel-number stack is the most-shared abundance frequency on YouTube. Excellent as a background loop while you work — let the carrier waves do the rewiring.",
    tags: ["888 Hz", "Abundance", "Frequency"],
  },
  {
    id: "ZToicYcHIOU",
    title: "Wealth & Abundance Frequency · 528 Hz Solfeggio",
    channel: "Meditative Mind",
    duration: "3 hr",
    views_label: "50M+ views",
    why: "The 'love frequency' 528 Hz tuned for prosperity flow. Best as a long background loop for deep work, study or sleep.",
    tags: ["528 Hz", "Solfeggio", "Abundance"],
  },
  {
    id: "MrlcEgS2e-Q",
    title: "528 Hz 'I AM' Affirmations · Wealth, Health, Prosperity",
    channel: "YouAreCreators / The Honest Guys (high-view)",
    duration: "1 hr",
    views_label: "Multi-million plays",
    why: "Affirmations spoken in the present-tense 'I AM' frame on a 528 Hz carrier — the script all the big manifestation coaches recommend.",
    tags: ["528 Hz", "Affirmations", "I AM"],
  },
  {
    id: "OqCTJ6YvSec",
    title: "Mahalakshmi Mantra · 108× (Anuradha Paudwal)",
    channel: "T-Series Bhakti Sagar",
    duration: "1 hr",
    views_label: "23M+ views",
    why: "The classical 108-rep Mahalakshmi chant by Anuradha Paudwal. The Vedic 'subliminal' for wealth karma — pair with a lit diya on Fridays.",
    tags: ["Mantra", "Lakshmi", "Vedic"],
  },
  {
    id: "PH0E1bfE54k",
    title: "Most Powerful Kubera Mantra · 108× for Wealth & Prosperity",
    channel: "Devotional Chants",
    duration: "1 hr",
    views_label: "Millions of plays",
    why: "Kubera is the cosmic treasurer. The 108-rep chant is the go-to for unblocking stuck income or stalled deals.",
    tags: ["Mantra", "Kubera", "Vedic"],
  },
  {
    id: "HIy3u02KQhw",
    title: "Attract Money, Success & Abundance While You Sleep",
    channel: "Sleep Meditation · Affirmations",
    duration: "8 hr",
    views_label: "Long-running favourite",
    why: "Sleep-cycle length so the affirmations enter the subconscious during REM. Earbuds optional, but a quiet room is essential.",
    tags: ["Sleep", "Affirmations", "Subconscious"],
  },
];

const HOW_TO_STEPS = [
  {
    icon: Headphones,
    title: "Use headphones for binaural tracks",
    detail:
      "Theta / binaural-beat sessions need stereo separation to produce the brainwave effect. For mantra and 432/528 Hz tracks, speakers are fine.",
  },
  {
    icon: PlayCircle,
    title: "21-day commitment, minimum 20 min/day",
    detail:
      "Subliminal programming is reps × consistency. Play one track in the background while you work, journal, or fall asleep — every day for 21 days.",
  },
  {
    icon: Sparkle,
    title: "Pair with a Friday Lakshmi ritual",
    detail:
      "Friday is Venus's day — the planet of wealth and beauty. Light a ghee diya, place a clean kalash, and play the Lakshmi mantra track once through.",
  },
];

function TrackCard({ track, active, onPlay }) {
  const youtubeUrl = `https://www.youtube.com/watch?v=${track.id}`;
  return (
    <article
      className="card-surface rounded-2xl overflow-hidden border border-amber-200 flex flex-col"
      data-testid={`wealth-track-${track.id}`}
    >
      <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
        {active ? (
          <iframe
            src={`https://www.youtube.com/embed/${track.id}?autoplay=1&rel=0&modestbranding=1`}
            title={track.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={onPlay}
            className="absolute inset-0 w-full h-full group"
            style={{
              backgroundImage: `url(https://i.ytimg.com/vi/${track.id}/hqdefault.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            data-testid={`wealth-track-play-${track.id}`}
            aria-label={`Play ${track.title}`}
          >
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/15 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-white/90 p-4 shadow-2xl group-hover:scale-110 transition-transform">
                <PlayCircle size={40} weight="fill" className="text-rose-600" />
              </div>
            </div>
            <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/50 text-white text-[10px] font-semibold tracking-wider">
              {track.duration}
            </span>
            <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold tracking-wider shadow">
              {track.views_label}
            </span>
          </button>
        )}
        {/* Permanent "Open on YouTube" pill — works whether the embed is
            playing, idle, OR throwing the "Sign in to confirm you're not a
            bot" check. Always a one-tap escape hatch to the canonical URL. */}
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/95 hover:bg-white text-rose-700 text-[11px] font-bold shadow-lg ring-1 ring-rose-200 transition-colors"
          data-testid={`wealth-track-youtube-${track.id}`}
          title="Open this track directly on YouTube"
        >
          <YoutubeLogo size={14} weight="fill" /> YouTube <ArrowSquareOut size={11} weight="bold" />
        </a>
      </div>
      <div className="p-5 flex flex-col gap-2.5 flex-1">
        <h3 className="font-serif-display text-lg leading-snug text-[#3F2D1B]">{track.title}</h3>
        <p className="text-xs text-muted">{track.channel}</p>
        <p className="text-sm text-foreground/85 leading-relaxed flex-1">
          <span className="font-semibold text-amber-900">Why this one:</span> {track.why}
        </p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {track.tags.map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 border border-amber-200 text-amber-900">
              {t}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-3 mt-1 border-t border-amber-100">
          {!active && (
            <button
              type="button"
              onClick={onPlay}
              className="inline-flex items-center justify-center gap-1.5 flex-1 px-3 py-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold transition-colors"
              data-testid={`wealth-track-play-inline-${track.id}`}
            >
              <PlayCircle size={14} weight="fill" /> Play here
            </button>
          )}
          <a
            href={youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 flex-1 px-3 py-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-md transition-colors"
            data-testid={`wealth-track-youtube-footer-${track.id}`}
          >
            <YoutubeLogo size={14} weight="fill" /> Watch on YouTube
          </a>
        </div>
      </div>
    </article>
  );
}

export default function WealthProgrammingPage() {
  const [activeId, setActiveId] = useState(null);

  return (
    <PageShell>
      <SEO
        title="Free Wealth Subliminal Programming · Top YouTube Tracks & Vedic Mantras — AstroKuber"
        description="Free curated playlist of the most-viewed wealth-subliminal programming videos on YouTube — Lakshmi & Kubera mantras, 432 Hz / 528 Hz solfeggio, theta binaural beats. 21-day rewire-your-money-story guide included. 100% free."
        path="/wealth-programming"
        keywords={[
          "wealth subliminal", "money subliminal", "money magnet subliminal", "wealth affirmations free",
          "abundance subliminal youtube", "best wealth subliminal", "money manifestation subliminal",
          "lakshmi mantra wealth", "kubera mantra sudden wealth", "528 hz abundance",
          "432 hz money", "theta binaural wealth", "sleep affirmations money",
          "subliminal programming", "wealth dna activation", "rewire money story",
        ]}
      />
      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-16" data-testid="wealth-programming-page">
        {/* HERO */}
        <header className="relative overflow-hidden rounded-3xl p-8 sm:p-12 mb-12"
          style={{
            background: "linear-gradient(135deg,#3F2D1B 0%,#7C2D12 45%,#B45309 100%)",
            color: "#FFF8E7",
          }}
        >
          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 text-[11px] font-bold tracking-wider mb-4">
              <CurrencyInr size={14} weight="bold" /> FREE WEALTH TOOLKIT · NO LOGIN NEEDED
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              Rewire your money story <span className="text-amber-300">with sound.</span>
            </h1>
            <p className="text-base sm:text-lg mt-5 text-amber-100/90 leading-relaxed font-light max-w-2xl">
              Six of the most-watched subliminal programming and Vedic mantra tracks on YouTube,
              curated by AstroKuber for daily wealth-mindset reprogramming. Play them in the
              background while you work, journal or sleep. Free, forever.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 text-xs text-amber-100/85">
              <span className="inline-flex items-center gap-1.5"><Star size={14} weight="fill" /> Curated picks</span>
              <span className="inline-flex items-center gap-1.5"><Lightning size={14} weight="fill" /> No signup required</span>
              <span className="inline-flex items-center gap-1.5"><Headphones size={14} weight="fill" /> Stream or download</span>
            </div>
          </div>
          {/* Decorative glow */}
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full opacity-30"
               style={{ background: "radial-gradient(circle, #FFD200 0%, transparent 70%)" }} />
          <div className="absolute -right-10 -bottom-20 w-80 h-80 rounded-full opacity-20"
               style={{ background: "radial-gradient(circle, #FFFFFF 0%, transparent 70%)" }} />
        </header>

        {/* FREE-CHAT CTA — placed above "How to use these tracks" per user
            request, so visitors see the conversion path immediately after
            the hero. Bright emerald gradient stands out against the warm
            brown hero above and the cream "how to use" cards below. */}
        <section
          className="relative overflow-hidden rounded-3xl p-7 sm:p-9 mb-12"
          style={{
            background: "linear-gradient(135deg,#064E3B 0%,#047857 45%,#10B981 100%)",
            color: "#ECFDF5",
          }}
          data-testid="wealth-free-chat-cta"
        >
          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-[11px] font-extrabold tracking-wider mb-3">
                <Sparkle size={14} weight="fill" /> FIRST 3 MINUTES FREE · NO PAYMENT NEEDED
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-light leading-tight">
                Want a live read on your wealth chart?{" "}
                <span className="text-amber-200">Chat with our astrologers free.</span>
              </h2>
              <p className="text-emerald-50/90 mt-3 text-sm sm:text-base leading-relaxed font-light max-w-2xl">
                Subliminals open the channel — a Vedic specialist tells you exactly which planet,
                house and dasha is moving money in your chart right now. Your first three minutes
                are on us.
              </p>
            </div>
            <Link
              to="/astrologers"
              data-testid="wealth-free-chat-button"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white hover:bg-emerald-50 text-emerald-800 font-bold text-sm sm:text-base tracking-wide shadow-2xl ring-2 ring-white/70 transition-all whitespace-nowrap"
            >
              <Sparkle size={18} weight="fill" /> Chat free with an astrologer <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          {/* Decorative glow */}
          <div className="absolute -right-12 -top-12 w-72 h-72 rounded-full opacity-30"
               style={{ background: "radial-gradient(circle, #FFFFFF 0%, transparent 70%)" }} />
        </section>

        {/* HOW TO USE */}
        <section className="mb-12">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-light text-[#3F2D1B]">How to use these tracks</h2>
          <p className="text-muted text-sm mt-2 max-w-2xl">
            Subliminals work through reps. The seekers who see real shifts use them DAILY,
            not occasionally — and pair them with a small Vedic action.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {HOW_TO_STEPS.map(({ icon: Icon, title, detail }, i) => (
              <div key={i} className="card-surface rounded-2xl p-5 border border-amber-200" data-testid={`wealth-howto-${i}`}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-800 mb-3">
                  <Icon size={20} weight="duotone" />
                </div>
                <h3 className="font-serif-display text-lg text-[#3F2D1B]">{title}</h3>
                <p className="text-sm text-foreground/80 mt-1.5 leading-relaxed">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TRACKS GRID */}
        <section className="mb-14">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="label-eyebrow text-[#7C2D12]">The playlist</div>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-light text-[#3F2D1B] mt-1">
                6 hand-picked wealth tracks
              </h2>
            </div>
            <div className="text-xs text-muted inline-flex items-center gap-1.5">
              <Info size={14} weight="duotone" /> Tap any thumbnail to play in-page
            </div>
          </div>
          <div
            className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50/70 p-4"
            data-testid="wealth-yt-bot-banner"
          >
            <Info size={20} weight="duotone" className="text-amber-700 mt-0.5 shrink-0" />
            <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
              <b>If YouTube asks "Sign in to confirm you're not a bot",</b> just tap the red{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-bold align-middle">
                <YoutubeLogo size={10} weight="fill" /> YouTube
              </span>{" "}
              button on any card. That always works — even when signed into AstroKuber.
              It opens the track in a new tab in your own YouTube session.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wealth-tracks-grid">
            {TRACKS.map((track) => (
              <TrackCard
                key={track.id}
                track={track}
                active={activeId === track.id}
                onPlay={() => setActiveId(track.id)}
              />
            ))}
          </div>
        </section>

        {/* RAHU CTA */}
        <section
          className="relative overflow-hidden rounded-3xl p-8 sm:p-12 mb-6"
          style={{
            background: "linear-gradient(135deg,#0F0716 0%,#3B1361 45%,#7C2D12 100%)",
            color: "#FFF8E7",
          }}
          data-testid="rahu-cta-section"
        >
          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-400/20 border border-violet-300/40 text-violet-200 text-[11px] font-bold tracking-wider mb-4">
                <Rocket size={14} weight="bold" /> NEXT-LEVEL · YOUR PERSONAL WEALTH MAP
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
                Subliminals open the channel. <br className="hidden sm:block" />
                <span className="text-amber-300">Rahu tells you where the money actually flows for YOU.</span>
              </h2>
              <p className="text-amber-100/90 mt-5 text-base leading-relaxed font-light max-w-2xl">
                Every birth chart has Rahu sitting in a specific house — the exact axis of obsessive,
                magnetic, sudden wealth in this lifetime. Your Rahu placement is the difference between
                a hard ten-year grind and a six-month vertical leap.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-xl">
                {[
                  "House-by-house wealth playbook for your exact Rahu",
                  "Career fields where your Rahu spikes income hardest",
                  "Avoid-this-or-you-will-underearn warnings",
                  "When your Rahu dasha amplifies it all",
                ].map((line) => (
                  <div key={line} className="flex items-start gap-2 text-amber-100/85 text-sm">
                    <Sparkle size={14} weight="fill" className="text-amber-300 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/rahu-placement"
                data-testid="wealth-rahu-cta"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#1A1410] font-bold text-sm sm:text-base tracking-wide shadow-2xl ring-2 ring-amber-200/60 hover:ring-amber-100 transition-all"
              >
                <Rocket size={18} weight="fill" /> Unlock my Rahu Wealth Playbook
                <ArrowRight size={16} weight="bold" />
              </Link>
              <p className="text-[11px] text-amber-200/70 mt-2.5">Free · No payment needed · Takes ~30 seconds</p>
            </div>
            <div className="hidden lg:flex justify-end">
              <div className="relative">
                <div className="absolute inset-0 rounded-full opacity-40 blur-3xl"
                     style={{ background: "radial-gradient(circle, #C084FC 0%, transparent 70%)" }} />
                <div className="relative w-40 h-40 rounded-full bg-violet-600/20 border border-violet-300/30 flex items-center justify-center">
                  <Rocket size={70} weight="duotone" className="text-amber-300" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECONDARY CTAS */}
        <section className="grid sm:grid-cols-2 gap-4 mb-10">
          <Link
            to="/numerology-deep"
            className="card-surface rounded-2xl p-5 border border-amber-200 hover:border-amber-400 transition-colors flex items-center justify-between gap-3 group"
            data-testid="wealth-secondary-numerology"
          >
            <div>
              <div className="label-eyebrow text-[#7C2D12]">Also free</div>
              <p className="font-serif-display text-lg mt-1 text-[#3F2D1B]">Deep Numerology & Lo Shu</p>
              <p className="text-xs text-muted mt-1">Driver, Loshu, lucky mobile / house / vehicle numbers tuned for your DOB.</p>
            </div>
            <ArrowRight size={20} weight="bold" className="text-amber-700 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/astrologers"
            className="card-surface rounded-2xl p-5 border border-emerald-300 bg-emerald-50/30 hover:border-emerald-500 transition-colors flex items-center justify-between gap-3 group"
            data-testid="wealth-secondary-chat"
          >
            <div>
              <div className="label-eyebrow text-emerald-800">Chat live</div>
              <p className="font-serif-display text-lg mt-1 text-[#1F4D2C]">Talk to a wealth-yoga specialist</p>
              <p className="text-xs text-muted mt-1">First 3 minutes free — get a live read on your Dhana, Lakshmi & Raja yogas.</p>
            </div>
            <ArrowRight size={20} weight="bold" className="text-emerald-700 group-hover:translate-x-1 transition-transform" />
          </Link>
        </section>

        {/* DISCLAIMER */}
        <p className="text-[11px] text-muted leading-relaxed text-center max-w-2xl mx-auto">
          Tracks are embedded directly from YouTube and remain the property of their respective creators.
          AstroKuber is not affiliated with any of the linked channels — this is a curated discovery aid only.
        </p>
      </main>
    </PageShell>
  );
}
